import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../services/bookingApi'; // Import de l'API services
import './PackManagement.css';

const PackManagement = () => {
  const [packs, setPacks] = useState([]);
  const [availableServices, setAvailableServices] = useState([]); // Services disponibles
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    description: '',
    validityPeriod: '1 year',
    includesChildren: false,
    maxChildren: 0,
    services: [] // Liste des IDs de services
  });
  const [editingPack, setEditingPack] = useState(null);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('create'); // 'create' or 'list'

  // Charger tous les packs et les services
  useEffect(() => {
    fetchPacks();
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const data = await api.getServices();
      setAvailableServices(data);
    } catch (error) {
      console.error('Erreur chargement services:', error);
    }
  };

  const fetchPacks = async () => {
    try {
      const response = await axios.get('/api/pack/getAllAdmin');
      setPacks(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des packs:', error);
      setMessage('❌ Impossible de charger les packs.');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setIsSubmitting(true);

    try {
      if (editingPack) {
        // Mise à jour du pack
        await axios.put(`/api/pack/update/${editingPack._id}`, formData);
        setMessage('✅ Pack mis à jour avec succès!');
      } else {
        // Création d'un nouveau pack
        await axios.post('/api/pack/create', formData);
        setMessage('✅ Pack créé avec succès!');
      }

      // Réinitialiser le formulaire
      setFormData({
        name: '',
        price: '',
        description: '',
        validityPeriod: '1 year',
        includesChildren: false,
        maxChildren: 0,
        services: []
      });
      setEditingPack(null);

      // Recharger la liste des packs
      fetchPacks();

      // Retourner à l'onglet liste
      setActiveTab('list');

    } catch (error) {
      console.error('Erreur détaillée:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur de connexion au serveur';
      setMessage('❌ ' + errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (pack) => {
    setFormData({
      name: pack.name,
      price: pack.price,
      description: pack.description,
      validityPeriod: pack.validityPeriod,
      includesChildren: pack.includesChildren,
      maxChildren: pack.maxChildren,
      services: pack.services ? pack.services.map(s => s._id || s) : []
    });
    setEditingPack(pack);
    setActiveTab('create');
  };

  const handleDelete = async (packId) => {
    if (window.confirm('Êtes-vous sûr de vouloir désactiver ce pack ?')) {
      try {
        await axios.put(`/api/pack/deactivate/${packId}`);
        setMessage('✅ Pack désactivé avec succès!');
        fetchPacks();
      } catch (error) {
        console.error('Erreur lors de la désactivation:', error);
        setMessage('❌ Erreur lors de la désactivation du pack.');
      }
    }
  };

  const handleActivate = async (packId) => {
    try {
      await axios.put(`/api/pack/update/${packId}`, { isActive: true });
      setMessage('✅ Pack activé avec succès!');
      fetchPacks();
    } catch (error) {
      console.error('Erreur lors de l\'activation:', error);
      setMessage('❌ Erreur lors de l\'activation du pack.');
    }
  };

  const cancelEdit = () => {
    setFormData({
      name: '',
      price: '',
      description: '',
      validityPeriod: '1 year',
      includesChildren: false,
      maxChildren: 0,
      services: []
    });
    setEditingPack(null);
  };

  return (
    <div className="pack-management-container">
      <div className="pack-management-card">
        <h2 className="pack-management-title">
          <span className="pack-icon">📦</span>
          Gestion des Packs d'Abonnement
        </h2>

        {message && (
          <div className={`pack-message ${message.includes('❌') ? 'pack-error' : 'pack-success'}`}>
            {message}
          </div>
        )}

        {/* Navigation par onglets */}
        <div className="pack-tabs">
          <button
            className={`pack-tab ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
          >
            {editingPack ? 'Modifier le Pack' : 'Créer un Pack'}
          </button>
          <button
            className={`pack-tab ${activeTab === 'list' ? 'active' : ''}`}
            onClick={() => setActiveTab('list')}
          >
            Liste des Packs ({packs.length})
          </button>
        </div>

        {/* Formulaire de création/modification */}
        {activeTab === 'create' && (
          <form className="pack-form" onSubmit={handleSubmit}>
            <div className="pack-form-grid">
              {/* Nom du pack */}
              <div className="pack-form-group">
                <label htmlFor="name" className="pack-form-label">
                  Nom du Pack *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="pack-form-input"
                  placeholder="Ex: Pack Famille, Pack Premium..."
                  required
                />
              </div>

              {/* Prix */}
              <div className="pack-form-group">
                <label htmlFor="price" className="pack-form-label">
                  Prix (DT) *
                </label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  min="0"
                  step="10"
                  className="pack-form-input"
                  required
                />
              </div>

              {/* Période de validité */}
              <div className="pack-form-group">
                <label htmlFor="validityPeriod" className="pack-form-label">
                  Période de Validité *
                </label>
                <select
                  id="validityPeriod"
                  name="validityPeriod"
                  value={formData.validityPeriod}
                  onChange={handleChange}
                  className="pack-form-select"
                  required
                >
                  <option value="6 months">6 Mois</option>
                  <option value="1 year">1 An</option>
                  <option value="2 years">2 Ans</option>
                </select>
              </div>

              {/* Inclut des enfants */}
              <div className="pack-form-group">
                <label className="pack-form-label">
                  Options
                </label>
                <div className="pack-checkbox-group">
                  <label className="pack-checkbox-label">
                    <input
                      type="checkbox"
                      name="includesChildren"
                      checked={formData.includesChildren}
                      onChange={handleChange}
                      className="pack-checkbox"
                    />
                    Inclut des enfants
                  </label>
                </div>
              </div>

              {/* Nombre maximum d'enfants */}
              {formData.includesChildren && (
                <div className="pack-form-group">
                  <label htmlFor="maxChildren" className="pack-form-label">
                    Nombre maximum d'enfants *
                  </label>
                  <select
                    id="maxChildren"
                    name="maxChildren"
                    value={formData.maxChildren}
                    onChange={handleChange}
                    className="pack-form-select"
                    required
                  >
                    <option value="0">0</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                  </select>
                </div>
              )}

              {/* Sélection des Services */}
              <div className="pack-form-group full-width">
                <label className="pack-form-label">Services inclus dans le pack</label>
                <div className="services-selection-grid" style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '10px',
                  border: '1px solid #ddd',
                  padding: '10px',
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {availableServices.map(service => (
                    <label key={service._id} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.services.includes(service._id)}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setFormData(prev => {
                            const currentServices = prev.services || [];
                            if (isChecked) {
                              return { ...prev, services: [...currentServices, service._id] };
                            } else {
                              return { ...prev, services: currentServices.filter(id => id !== service._id) };
                            }
                          });
                        }}
                        style={{ marginRight: '8px' }}
                      />
                      {service.name} ({service.duration}min)
                    </label>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div className="pack-form-group full-width">
                <label htmlFor="description" className="pack-form-label">
                  Description *
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="pack-form-textarea"
                  placeholder="Décrivez les avantages et caractéristiques de ce pack..."
                  rows="4"
                  required
                />
              </div>
            </div>

            <div className="pack-form-actions">
              {editingPack && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="pack-cancel-btn"
                >
                  Annuler
                </button>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="pack-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <span className="pack-spinner"></span>
                    {editingPack ? 'Mise à jour...' : 'Création...'}
                  </>
                ) : (
                  editingPack ? '📝 Modifier le Pack' : '➕ Créer le Pack'
                )}
              </button>
            </div>
          </form>
        )}

        {/* Liste des packs */}
        {activeTab === 'list' && (
          <div className="pack-list">
            {packs.length === 0 ? (
              <div className="pack-empty">
                Aucun pack créé pour le moment.
              </div>
            ) : (
              <div className="pack-grid">
                {packs.map((pack) => (
                  <div key={pack._id} className={`pack-card ${!pack.isActive ? 'inactive' : ''}`}>
                    <div className="pack-card-header">
                      <h3 className="pack-card-title">{pack.name}</h3>
                      <span className="pack-card-price">{pack.price} DT</span>
                    </div>

                    <div className="pack-card-details">
                      <div className="pack-card-detail">
                        <strong>Validité:</strong> {pack.validityPeriod}
                      </div>
                      <div className="pack-card-detail">
                        <strong>Enfants inclus:</strong> {pack.includesChildren ? `${pack.maxChildren} enfant(s)` : 'Non'}
                      </div>
                      <div className="pack-card-detail">
                        <strong>Statut:</strong>
                        <span className={`pack-status ${pack.isActive ? 'active' : 'inactive'}`}>
                          {pack.isActive ? 'Actif' : 'Inactif'}
                        </span>
                      </div>
                    </div>

                    <div className="pack-card-description">
                      {pack.description}
                    </div>

                    <div className="pack-card-actions">
                      <button
                        onClick={() => handleEdit(pack)}
                        className="pack-edit-btn"
                      >
                        ✏️ Modifier
                      </button>
                      {pack.isActive ? (
                        <button
                          onClick={() => handleDelete(pack._id)}
                          className="pack-delete-btn"
                        >
                          🗑️ Désactiver
                        </button>
                      ) : (
                        <button
                          onClick={() => handleActivate(pack._id)}
                          className="pack-activate-btn"
                        >
                          ✅ Activer
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PackManagement;
